import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AddCertificateTypeDialog } from '@/components/admin/certificates/AddCertificateTypeDialog';
import { EditCertificateTypeDialog } from '@/components/admin/certificates/EditCertificateTypeDialog';
import { toast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

type CertificateType = {
  id: string;
  name: string;
  description: string | null;
  display_order: number;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
};

export function CertificateTypesList() {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedCertificateType, setSelectedCertificateType] = useState<CertificateType | null>(null);
  
  const { data: certificateTypes, isLoading, error, refetch } = useQuery({
    queryKey: ['certificateTypes'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('certificate_types')
        .select('*')
        .order('display_order', { ascending: true });
      
      if (error) throw error;
      return data;
    },
  });

  const handleEditClick = (type: CertificateType) => {
    setSelectedCertificateType(type);
    setIsEditDialogOpen(true);
  };

  const handleDeleteClick = (type: CertificateType) => {
    setSelectedCertificateType(type);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedCertificateType) return;
    
    try {
      const { error } = await supabase
        .from('certificate_types')
        .delete()
        .eq('id', selectedCertificateType.id);
      
      if (error) {
        toast.error("Error", error.message);
        return;
      }
      
      toast.success("Success", "Certificate type deleted successfully");
      
      // Close dialog and refresh data
      setIsDeleteDialogOpen(false);
      refetch();
    } catch (error) {
      console.error('Error deleting certificate type:', error);
      toast.error("Error", "An unexpected error occurred");
    }
  };
  
  if (isLoading) {
    return <div>Loading certificate types...</div>;
  }
  
  if (error) {
    return <div>Error loading certificate types</div>;
  }
  
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setIsAddDialogOpen(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add Type
        </Button>
      </div>
      
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Description</TableHead>
            <TableHead className="text-center">Status</TableHead>
            <TableHead className="text-center">Display Order</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {certificateTypes && certificateTypes.length > 0 ? (
            certificateTypes.map((type) => (
              <TableRow key={type.id}>
                <TableCell className="font-medium">{type.name}</TableCell>
                <TableCell>{type.description || '-'}</TableCell>
                <TableCell className="text-center">
                  <span className={`px-2 py-1 rounded-full text-xs ${type.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                    {type.is_active ? 'Active' : 'Inactive'}
                  </span>
                </TableCell>
                <TableCell className="text-center">{type.display_order}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleEditClick(type)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleDeleteClick(type)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={5} className="text-center py-4">No certificate types found</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      
      <AddCertificateTypeDialog 
        open={isAddDialogOpen} 
        onOpenChange={setIsAddDialogOpen} 
        onSuccess={refetch}
      />

      <EditCertificateTypeDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        certificateType={selectedCertificateType}
        onSuccess={refetch}
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the certificate type.
              Any certificates using this type might be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
